import { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import HourglassEmptyOutlined from '@mui/icons-material/HourglassEmptyOutlined';
import MarkEmailUnreadOutlined from '@mui/icons-material/MarkEmailUnreadOutlined';
import TaskAltOutlined from '@mui/icons-material/TaskAltOutlined';
import { StatTile } from '../components/StatTile';
import { TabStrip } from '../components/TabStrip';
import { WithSideColumn } from '../components/WithSideColumn';
import { ActivityFeed } from '../features/communications/ActivityFeed';
import { useActivityFilters } from '../features/communications/activityFilters';
import { CommunicationsList } from '../features/communications/CommunicationsList';
import { QuickStats, StatusList } from '../features/communications/OverviewSide';
import { type ActivityStatus } from '../features/communications/sample';
import { useCommunications } from '../features/communications/useCommunications';

const GAP = { xs: 1.5, md: 2 };
const TABS = ['Recent Activity', 'By Shipment'] as const;

/** Communications at a glance: what needs you, what is waiting, and the latest messages. A click opens the conversation. */
export function CommunicationsPage() {
  const navigate = useNavigate();
  const { events, rows } = useCommunications();
  const filters = useActivityFilters();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Recent Activity');
  const count = (status: ActivityStatus) => events.filter((e) => e.status === status).length;
  const show = (status: ActivityStatus) => () => {
    filters.setStatus(status);
    setTab('Recent Activity');
  };

  return (
    <Stack spacing={GAP}>
      <Box>
        <Typography variant="h4" component="h1">
          Communications
        </Typography>
        <Typography color="text.secondary">
          Keep track of all important communications across your shipments.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: { xs: 1.25, md: 2 },
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
        }}
      >
        <StatTile
          count={count('Needs Attention')}
          title="Needs Attention"
          note="Require your action or response"
          icon={<ErrorOutlineOutlined />}
          tone="error"
          onClick={show('Needs Attention')}
        />
        <StatTile
          count={count('Awaiting Reply')}
          title="Awaiting Replies"
          note="Sent and waiting for a response"
          icon={<HourglassEmptyOutlined />}
          tone="warning"
          onClick={show('Awaiting Reply')}
        />
        <StatTile
          count={count('New Update')}
          title="New Updates"
          note="Received since your last visit"
          icon={<MarkEmailUnreadOutlined />}
          tone="info"
          onClick={show('New Update')}
        />
        <StatTile
          count={count('Pending Approval')}
          title="Pending Approvals"
          note="Draft messages for your review"
          icon={<TaskAltOutlined />}
          tone="neutral"
          to="/approvals"
        />
      </Box>

      <WithSideColumn
        side={
          <>
            <StatusList status="Needs Attention" title="Needs Attention" />
            <StatusList status="Awaiting Reply" title="Awaiting Replies" />
            <StatusList status="Pending Approval" title="Pending Approvals" viewAll="/approvals" />
            <QuickStats />
          </>
        }
      >
        <TabStrip tabs={TABS.map((t) => ({ value: t }))} value={tab} onChange={setTab} />
        {tab === 'Recent Activity' ? (
          <ActivityFeed filters={filters} />
        ) : (
          <Box sx={{ maxWidth: 720 }}>
            <CommunicationsList rows={rows} onSelect={(srb) => void navigate(`/communications/${srb}`)} />
          </Box>
        )}
      </WithSideColumn>
    </Stack>
  );
}
